import { configureStore, getDefaultMiddleware } from '@reduxjs/toolkit';

import { useDispatch, useSelector } from 'react-redux';

import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  PersistConfig,
} from 'redux-persist';

import storage from 'redux-persist/lib/storage';

import { IRootState, rootReducer } from './features/rootReducer';

type Persisted = PersistConfig<IRootState>;
type MigrateFn = NonNullable<Persisted['migrate']>;
type MigrateState = Awaited<ReturnType<MigrateFn>>;
type ActualMigrateState = IRootState & MigrateState;

// https://redux.js.org/usage/structuring-reducers/initializing-state
const persistedReducer = persistReducer<IRootState>(
  {
    key: 'root',
    version: 1,
    storage,
    whitelist: [
      'auth',
      'ticket',
      'notification',
      'userConfiguration',
      'organization',
      'search',
      'updatesOptimistic',
    ],
    // Type issues, see: https://github.com/rt2zz/redux-persist/issues/1065
    migrate: state => {
      if (!state) return Promise.resolve(state);
      // For redux persistor.
      const t = state as NonNullable<ActualMigrateState>;
      const updatesOptimistic: ActualMigrateState['updatesOptimistic'] = {
        deletedUpdates: t.updatesOptimistic?.deletedUpdates || [],
        newUpdates: [],
        updatedUpdates: [],
        backupUpdatedUpdates: t.updatesOptimistic?.backupUpdatedUpdates || [],
        backupNewUpdates: t.updatesOptimistic?.backupNewUpdates || [],
      };

      const newState = {
        ...t,
        updatesOptimistic: {
          ...t.updatesOptimistic,
          ...updatesOptimistic,
        },
      };

      return Promise.resolve(newState);
    },
  },
  rootReducer,
);

const store = configureStore({
  reducer: persistedReducer,
  middleware: getDefaultMiddleware({
    serializableCheck: {
      ignoredActions: [
        FLUSH,
        REHYDRATE,
        PAUSE,
        PERSIST,
        PURGE,
        REGISTER,
        'Known Data/addKnownFromEquipments',
        'Known Data/addKnownEquipments',
      ],
    },
  }),
});

const persistor = persistStore(store);
export type ReduxStore = typeof store;
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector = () => useSelector((state: RootState) => state);

if (window.Cypress) {
  window.__store__ = store;
}

export { store, persistor };
